import { Event } from '@yandex-int/messenger.utils';
import { select } from 'typed-redux-saga';

import { getStore } from '../../../../init-store';
import { isIncomingPostMessage } from '../../../pages/call/iframe-sagas/helpers';
import { IncomingPostMessage, OutgoingPostMessage } from '../../../pages/call/iframe-sagas/types';
import { setChatWidgetAuthorizationToken } from '../../helpers';
import { bind } from '../../helpers/decorators';
import { sendPostMessage } from '../../helpers/iframe';
import { createLogger } from '../../helpers/loggerFacade';
import { chatApi, cloudApi } from '../../helpers/transport';
import { selectIsMeetingRoomIntegration } from '../config/selectors';
import { isInitConnectionMessage } from './helpers';
import { initConnectionMessageReceivedAction } from './slice';
import {
    ParentFrameCommunicatorPendingMessages,
    ParentFrameCommunicatorPorts,
    ParentFrameEndpoint,
    SupportedParentFrame,
} from './types';

const logger = createLogger('iframe-manager');

class ParentFrameCommunicator {
    private ports: ParentFrameCommunicatorPorts = {};
    private pendingMessages: ParentFrameCommunicatorPendingMessages = {};
    private wasInit = false;

    public onMessage = new Event<IncomingPostMessage>();

    @bind
    public *init() {
        if (this.wasInit) {
            return;
        }
        this.wasInit = true;

        if (yield* select(selectIsMeetingRoomIntegration)) {
            this.ports['meeting-room'] = null;
        }

        // Раскоментировать при переходе messenger на использование порта
        // if (yield* select(selectIsMessengerIntegration)) {
        //     this.ports.messenger = null;
        // }

        if (Object.keys(this.ports).length) {
            window.addEventListener('message', this.subscribeOnPort);
            return () => {
                window.removeEventListener('message', this.subscribeOnPort);
            };
        }

        const handleMessageFromWindow = this.handleMessage('window');
        window.addEventListener('message', handleMessageFromWindow);

        return () => {
            window.removeEventListener('message', handleMessageFromWindow);
        };
    }

    @bind
    private subscribeOnPort(event: MessageEvent) {
        if (isInitConnectionMessage(event)) {
            window.removeEventListener('message', this.subscribeOnPort);

            const serviceName = event.data.name;

            const port = event.ports[0];
            port.onmessage = this.handleMessage(serviceName);
            this.ports[event.data.name] = port;

            // Проверяем наличие авторизационного токена и применяем его, если есть
            const token = event.data.authToken;
            if (token) {
                cloudApi.setApiAuthorizationHeader(`OAuth ${token}`);
                cloudApi.disableCredentials();
                chatApi.setApiAuthorizationHeader(token);
                chatApi.disableCredentials();
                setChatWidgetAuthorizationToken(token);
            }

            const store = getStore();
            store.dispatch(initConnectionMessageReceivedAction());

            // Отправляем все ожидающие сообщения
            this.pendingMessages[serviceName]?.forEach((data) => {
                this.sendMessage(serviceName, data.message, data.serialize);
            });
            delete this.pendingMessages[serviceName];
        }
    }

    @bind
    private handleMessage(sender: ParentFrameEndpoint) {
        return (event: MessageEvent) => {
            try {
                const message = JSON.parse(event.data);

                if (isIncomingPostMessage(message)) {
                    logger.event('Incoming iframe message', {
                        additional: {
                            method: 'handleMessage',
                            target: sender,
                            ...(message ?? {}),
                        },
                    });
                    this.onMessage.dispatch(message);
                }
            } catch {
                return;
            }
        };
    }

    @bind
    public sendMessage(
        receiver: ParentFrameEndpoint | ParentFrameEndpoint[] | '*',
        message: OutgoingPostMessage,
        serialize?: boolean
    ) {
        // Если получили '*', то перечисляем все возможные значения и вызываем снова sendMessage.
        // Чтобы сервис попал в список перечисления, у его порта должно быть значение null или MessagePort
        if (receiver === '*') {
            const ports = Object.keys(this.ports) as SupportedParentFrame[];
            this.sendMessage(ports.length ? ports : 'window', message, serialize);

            return;
        }

        // Если получили массив, то для каждого элемента в receiver отдельно вызываем sendMessage
        if (Array.isArray(receiver)) {
            receiver.forEach((target) => {
                this.sendMessage(target, message, serialize);
            });

            return;
        }

        // Отправка сообщения для window, а также messenger, если он не прислал порт
        if (receiver === 'window' || (receiver === 'messenger' && this.ports.messenger === undefined)) {
            logger.event('Outgoing iframe message', {
                additional: {
                    method: 'sendMessage',
                    target: receiver,
                    ...(message ?? {}),
                },
            });

            sendPostMessage(message, '*', serialize);
            return;
        }

        // Если для сервиса не открыт порт (значение null), то добавляем сообщение в отложенную отправку
        if (!this.ports[receiver]) {
            logger.event('Outgoing iframe message added to pending list', {
                additional: {
                    method: 'sendMessage',
                    target: receiver,
                    ...(message ?? {}),
                },
            });

            const messages = this.pendingMessages[receiver] || [];
            messages.push({ message, serialize });
            this.pendingMessages[receiver] = messages;

            return;
        }

        // Отправляем сообщение в целевой сервис
        logger.event('Outgoing iframe message', {
            additional: {
                method: 'sendMessage',
                target: receiver,
                ...(message ?? {}),
            },
        });

        try {
            this.ports[receiver]?.postMessage(serialize ? JSON.stringify(message) : message);
        } catch (error) {
            logger.error(error, {
                method: 'sendMessage',
                additional: {
                    target: receiver,
                    ...(message ?? {}),
                },
            });
        }
    }
}

export const parentFrameCommunicator = new ParentFrameCommunicator();
